import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { setFilters } from '../router.js';
import { apiParams } from '../lib/filters.js';
import { STATUS_STATES } from '../lib/palette.js';
import {
  CHANGES_PARAM_KEYS,
  CHANGES_SHOWN,
  LABEL_GAP,
  SLOPE_CAP,
  bandState,
  causeLabel,
  causeSentence,
  cellExportRows,
  changeExportRows,
  changesSummary,
  compareRoleLabel,
  placeLabels,
  signedHours,
  slopeAxisMax,
  slopeDimension,
  slopeExportRows,
  slopeLines,
  sortChanges,
  truncateLabel,
  changeCellColumns,
  changeListColumns,
  slopeColumns,
} from '../lib/changes.js';
import { withFormats } from '../lib/table.js';
import { hours as formatHours, percent, weekLabel, count, humanize } from '../lib/format.js';

/** The noun a dimension's rows go by, for the subtitle and the KPIs. */
const NOUNS = {
  service: ['role', 'roles'],
  department: ['department', 'departments'],
  office: ['office', 'offices'],
  individual: ['person', 'people'],
  company: ['company', 'company'],
};
const noun = (dimension, n) => (NOUNS[dimension] ?? ['row', 'rows'])[n === 1 ? 0 : 1];

/** Where the two axes stand across the plot, as shares of its width: room for names on the right. */
const AXIS_FROM = 0.22;
const AXIS_TO = 0.56;

/** The words beside each threshold line. */
const THRESHOLD_WORDS = {
  underload_below: 'under-utilized below',
  caution_above: 'caution above',
  overloaded_above: 'overloaded above',
};

const exact = (h) => formatHours(h, { exact: true });

/**
 * The Changes view (V-13, Phase 30; PRD 10.8): what moved since the last forecast, and why.
 * Two things from one response, `GET /forecast/diff`: the slope chart — one line per row of the
 * dimension from its peak allocation in the compared run to its peak in this one, the lines
 * that crossed a threshold emphasised and the ones that did not change faded — and beneath it
 * the attributed list, largest movement first, each change with the cause the diff filed it
 * under and the evidence behind that in a sentence.
 *
 * The card adds no arithmetic: the ends are the two runs' own heatmap peaks as the API states
 * them, the emphasis is the API's `peak_band_changed`, the list is the API's order. What it
 * decides is presentation — which dimension a slope chart of "roles" asks for when the filter
 * bar says company, where the axis stops so the thresholds stay readable, where a label goes so
 * it does not sit on its neighbour — and every one of those rules is in `lib/changes.js`, where
 * the tests can hold it still.
 *
 * Lines are drawn as keyed groups (`repeat` on the entity key), so a filter change updates the
 * survivors in place rather than repainting them (PRD 7.3). The marks are rendered by
 * `chart-frame` inside *its* shadow root, so every mark carries its presentation as attributes.
 */
class SnapshotChanges extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _noComparison: { state: true },
    _hover: { state: true },
    _slopeOpen: { state: true },
    _listOpen: { state: true },
    _cellsOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._noComparison = false;
    this._hover = null;
    this._slopeOpen = false;
    this._listOpen = true;
    this._cellsOpen = false;
    this._requestKey = null;
    this._slope = null;
    this._slopeFor = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 20px;
        margin-top: 10px;
      }
      .key {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12.5px;
        color: var(--ink);
      }
      .key svg {
        display: block;
      }
      .key .icon {
        font-size: 10px;
        line-height: 1;
      }
      .section-title {
        margin: 18px 0 0;
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      .causes {
        margin: 8px 0 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-wrap: wrap;
        gap: 6px 10px;
      }
      .causes li {
        font-size: 12px;
        color: var(--ink);
        background: var(--surface-2);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-pill);
        padding: 2px 9px;
        font-variant-numeric: tabular-nums;
      }
      .causes li b {
        font-weight: 600;
      }
      ol.changes {
        margin: 8px 0 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 6px;
      }
      ol.changes li {
        display: grid;
        grid-template-columns: 6.5em minmax(0, 1fr);
        gap: 2px 12px;
        align-items: baseline;
        font-size: 13px;
        color: var(--ink);
        padding: 6px 0;
        border-top: 1px solid var(--border-soft);
      }
      ol.changes li:first-child {
        border-top: 0;
      }
      ol.changes .delta {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--ink-strong);
        text-align: right;
        white-space: nowrap;
      }
      ol.changes .delta small {
        display: block;
        font-weight: 400;
        font-size: 11px;
        color: var(--ink-muted);
      }
      ol.changes .who {
        font-weight: 600;
        color: var(--ink-strong);
      }
      ol.changes .who.withheld {
        color: var(--ink-muted);
        font-style: italic;
      }
      ol.changes .chip {
        display: inline-block;
        margin-left: 8px;
        font-size: 11px;
        font-weight: 600;
        color: var(--ink-muted);
        background: var(--surface-2);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-pill);
        padding: 0 7px;
        vertical-align: 1px;
      }
      ol.changes .why {
        grid-column: 2;
        color: var(--ink);
        font-size: 12.5px;
      }
      ol.changes .meta {
        grid-column: 2;
        color: var(--ink-muted);
        font-size: 11.5px;
      }
      .shelf {
        margin: 8px 0 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 4px;
      }
      .shelf li {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 10px;
        align-items: baseline;
        font-size: 12.5px;
        color: var(--ink);
      }
      .shelf .delta {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--ink-strong);
        min-width: 5.5em;
        text-align: right;
      }
      .shelf .then {
        color: var(--ink-muted);
        font-variant-numeric: tabular-nums;
      }
      button.link {
        font: inherit;
        font-weight: 600;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
      }
      button.link:hover {
        text-decoration: underline;
      }
      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .state {
        padding: 36px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    // Fetch off the store, not off the render cycle: a filter or run change asks the API only
    // when a parameter this view reads has moved.
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /** The request this view wants for the current filters, or null when there is no run. */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    const params = apiParams(filters, CHANGES_PARAM_KEYS);
    const { dimension, substituted } = slopeDimension(filters.dimension);
    params.dimension = dimension;
    // An entity of the dimension that was substituted names nothing in the one asked for.
    if (substituted) delete params.entity;
    return params;
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    this._noComparison = false;
    try {
      const data = await api.get('/forecast/diff', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      // One run is not a comparison. The API says so with a code, and the card says it in words.
      if (err.code === 'NO_COMPARISON_RUN') this._noComparison = true;
      else this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Model

  /** The lines and the shelf, computed once per response. */
  get _slopeOf() {
    if (this._slopeFor !== this._data) {
      this._slope = slopeLines(this._data?.entities ?? []);
      this._slopeFor = this._data;
    }
    return this._slope;
  }

  get _lines() {
    return this._slopeOf.lines;
  }

  get _shelf() {
    return this._slopeOf.shelf;
  }

  get _changes() {
    return sortChanges(this._data?.changes ?? []);
  }

  get _axisMax() {
    return slopeAxisMax(this._lines, this._data?.thresholds);
  }

  get _summary() {
    return changesSummary(this._data);
  }

  /** The row the view is narrowed to, when the filter bar names one of this dimension. */
  get _narrowed() {
    return this._data?.entity ?? null;
  }

  // -------------------------------------------------------------------------------------------
  // Drawing

  _fmt() {
    return { hours: exact, date: weekLabel, percent };
  }

  _strokeOf(line, hovered) {
    const base = { crossed: 2.5, changed: 1.5, unchanged: 1 }[line.emphasis];
    return hovered ? base + 1 : base;
  }

  _inkOf(line) {
    return { crossed: 'var(--ink-strong)', changed: 'var(--ink)', unchanged: 'var(--ink-muted)' }[
      line.emphasis
    ];
  }

  _opacityOf(line, hovered) {
    if (hovered) return 1;
    return { crossed: 1, changed: 0.85, unchanged: 0.35 }[line.emphasis];
  }

  _dotFill(end) {
    const state = end ? bandState(end.band) : null;
    return state ? `var(${state.var})` : 'var(--ink-muted)';
  }

  _marks = ({ y, plot, svg: s }) => {
    const d = this._data;
    const lines = this._lines;
    const xf = plot.left + plot.width * AXIS_FROM;
    const xt = plot.left + plot.width * AXIS_TO;
    const bottom = plot.top + plot.height;
    const axisMax = this._axisMax;

    // Labels: every line where they fit, else only the ones that changed; each side placed so
    // no label sits on its neighbour.
    const fit = lines.length * LABEL_GAP <= plot.height;
    const labelled = fit ? lines : lines.filter((l) => l.emphasis !== 'unchanged');
    const rightY = placeLabels(
      labelled.filter((l) => l.to).map((l) => ({ key: l.key, y: y(l.to.drawn) })),
      { top: plot.top + 5, bottom: bottom - 2 },
    );
    const leftY = placeLabels(
      labelled.filter((l) => l.from).map((l) => ({ key: l.key, y: y(l.from.drawn) })),
      { top: plot.top + 5, bottom: bottom - 2 },
    );
    const rightChars = Math.max(10, Math.floor((plot.left + plot.width - xt - 12) / 6.2));
    const leftChars = Math.max(6, Math.floor((xf - plot.left - 12) / 6.2));

    const thresholds = Object.entries(d?.thresholds ?? {}).filter(
      ([k, v]) => THRESHOLD_WORDS[k] && Number(v) <= axisMax,
    );

    const label = (line, side) => {
      const end = side === 'to' ? line.to : line.from;
      if (!end) return nothing;
      const yy = side === 'to' ? rightY.get(line.key) : leftY.get(line.key);
      if (yy === undefined) return nothing;
      const state = bandState(end.band);
      const icon = state ? state.icon : '';
      const value = percent(end.value);
      // The name travels with the right end, and with the left one only where there is no right.
      const name =
        side === 'to' || line.left
          ? truncateLabel(line.label, (side === 'to' ? rightChars : leftChars) - value.length - 3)
          : '';
      const text =
        side === 'to' ? `${icon} ${value} ${name}`.trim() : `${name} ${value} ${icon}`.trim();
      const hovered = this._hover === line.key;
      return s`<text class=${`end-label ${side}`} data-entity=${line.key}
                     x=${side === 'to' ? xt + 10 : xf - 10} y=${yy} dy="0.32em"
                     text-anchor=${side === 'to' ? 'start' : 'end'}
                     fill=${this._inkOf(line)} opacity=${this._opacityOf(line, hovered)}
                     font-size="11" font-family="var(--font-sans)"
                     font-weight=${line.emphasis === 'crossed' || hovered ? '700' : '400'}
                     font-variant-numeric="tabular-nums" pointer-events="none">${text}</text>`;
    };

    const dot = (line, end, x) => {
      if (!end) return nothing;
      const hovered = this._hover === line.key;
      const r = line.emphasis === 'crossed' || hovered ? 4.5 : 3.5;
      return s`
        <circle class="end" data-band=${end.band ?? 'none'} cx=${x} cy=${y(end.drawn)} r=${r}
                fill=${this._dotFill(end)} stroke="var(--surface)" stroke-width="1.5"
                opacity=${this._opacityOf(line, hovered)} />
        ${
          end.pinned
            ? s`<text class="pin" x=${x} y=${y(end.drawn) - 8} text-anchor="middle"
                      fill=${this._inkOf(line)} font-size="9" font-family="var(--font-sans)"
                      pointer-events="none">▲</text>`
            : nothing
        }`;
    };

    return s`
      <g class="thresholds">
        ${thresholds.map(
          ([key, value]) => s`
            <line x1=${xf} x2=${xt} y1=${y(Number(value))} y2=${y(Number(value))}
                  stroke="var(--axis)" stroke-width="1" stroke-dasharray="3 3" />
            <text x=${(xf + xt) / 2} y=${y(Number(value)) - 4} text-anchor="middle"
                  fill="var(--ink-muted)" font-size="9.5" font-family="var(--font-sans)">
              ${THRESHOLD_WORDS[key]} ${percent(Number(value))}
            </text>`,
        )}
      </g>
      <g class="axes">
        <line class="axis-from" x1=${xf} x2=${xf} y1=${plot.top} y2=${bottom} stroke="var(--axis)" stroke-width="1" />
        <line class="axis-to" x1=${xt} x2=${xt} y1=${plot.top} y2=${bottom} stroke="var(--axis)" stroke-width="1" />
        <text class="axis-caption" x=${xf} y=${bottom + 16} text-anchor="middle"
              fill="var(--tick-ink)" font-size="11" font-family="var(--font-sans)">
          Run of ${weekLabel(d?.compare?.planning_date)}
        </text>
        <text class="axis-caption" x=${xt} y=${bottom + 16} text-anchor="middle"
              fill="var(--tick-ink)" font-size="11" font-family="var(--font-sans)" font-weight="600">
          Run of ${weekLabel(d?.planning_date)}
        </text>
      </g>
      <g class="lines">
        ${repeat(
          lines,
          (line) => line.key,
          (line) => {
            const hovered = this._hover === line.key;
            const both = line.from && line.to;
            return s`
              <g class="entity" data-entity=${line.key} data-emphasis=${line.emphasis}
                 data-arrived=${line.arrived ? 'true' : 'false'} data-left=${line.left ? 'true' : 'false'}>
                ${
                  both
                    ? s`<line class="slope" x1=${xf} y1=${y(line.from.drawn)} x2=${xt} y2=${y(line.to.drawn)}
                              stroke=${this._inkOf(line)} stroke-width=${this._strokeOf(line, hovered)}
                              opacity=${this._opacityOf(line, hovered)} stroke-linecap="round" />`
                    : nothing
                }
                ${dot(line, line.from, xf)}
                ${dot(line, line.to, xt)}
                ${
                  both
                    ? s`<line class="hit" x1=${xf} y1=${y(line.from.drawn)} x2=${xt} y2=${y(line.to.drawn)}
                              stroke="transparent" stroke-width="12" style="cursor: pointer"
                              tabindex="0" role="img" aria-label=${this._ariaOf(line)}
                              @pointermove=${(e) => this._showTooltip(e, line)}
                              @pointerleave=${() => this._hideTooltip()}
                              @focus=${(e) => this._showTooltip(e, line)}
                              @blur=${() => this._hideTooltip()}
                              @click=${() => this._narrowTo(line)}
                              @keydown=${(e) => e.key === 'Enter' && this._narrowTo(line)} />`
                    : s`<circle class="hit" cx=${line.to ? xt : xf}
                                cy=${y((line.to ?? line.from).drawn)} r="9"
                                fill="transparent" style="cursor: pointer"
                                tabindex="0" role="img" aria-label=${this._ariaOf(line)}
                                @pointermove=${(e) => this._showTooltip(e, line)}
                                @pointerleave=${() => this._hideTooltip()}
                                @focus=${(e) => this._showTooltip(e, line)}
                                @blur=${() => this._hideTooltip()}
                                @click=${() => this._narrowTo(line)}
                                @keydown=${(e) => e.key === 'Enter' && this._narrowTo(line)} />`
                }
              </g>`;
          },
        )}
      </g>
      <g class="labels">
        ${labelled.map((line) => label(line, 'from'))}
        ${labelled.map((line) => label(line, 'to'))}
      </g>
    `;
  };

  _ariaOf(line) {
    const from = line.from ? percent(line.from.value) : 'not in the compared run';
    const to = line.to ? percent(line.to.value) : 'not in this run';
    return `${line.label}: peak allocation ${from} then ${to} now${
      line.emphasis === 'crossed' ? ', crossed a threshold' : ''
    }`;
  }

  // -------------------------------------------------------------------------------------------
  // Hover and narrowing

  _showTooltip(event, line) {
    this._hover = line.key;
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const d = this._data;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const end = (e) =>
      e
        ? `${percent(e.value)} · week of ${weekLabel(e.week_start)} · ${exact(e.allocated_hours)} of ${exact(e.capacity_hours)}`
        : 'no peak: not in this run';
    const stateFrom = line.from ? bandState(line.from.band) : null;
    const stateTo = line.to ? bandState(line.to.band) : null;
    const rows = [
      { label: `Run of ${weekLabel(d.compare.planning_date)}`, value: end(line.from) },
      { label: `Run of ${weekLabel(d.planning_date)}`, value: end(line.to) },
      {
        label: 'Band',
        value: `${stateFrom ? `${stateFrom.icon} ${stateFrom.label}` : '—'} → ${
          stateTo ? `${stateTo.icon} ${stateTo.label}` : '—'
        }${line.emphasis === 'crossed' ? ' (crossed)' : ''}`,
        token: stateTo?.var,
        fill: stateTo ? 'solid' : undefined,
      },
      {
        label: 'Hours over the window',
        value: `${exact(line.hours_from)} → ${exact(line.hours_to)} (${signedHours(line.hours_delta, exact)})`,
      },
    ];
    if (line.cause)
      rows.push({
        label: 'Leading cause',
        value: `${causeLabel(line.cause)}${line.cause_entity ? ` — ${line.cause_entity.label}` : ''}`,
      });
    const footer = [
      `${count(line.weeks_changed)} of ${count(d.weeks_count)} weeks changed`,
      `${count(line.band_crossings)} band ${line.band_crossings === 1 ? 'crossing' : 'crossings'}`,
      this._narrowed === line.key ? 'click to widen' : 'click to narrow the list to it',
    ].join(' · ');
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: line.label,
      rows,
      footer,
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  /** Narrow the shared filters to one row of the dimension, or widen back if it is that row. */
  _narrowTo(line) {
    if (this._narrowed === line.key) setFilters({ entity: null });
    else setFilters({ dimension: this._data.dimension, entity: line.key });
  }

  // -------------------------------------------------------------------------------------------
  // The list

  _deltaOf(c) {
    // A person's capacity is measured in hours of capacity; everything else in hours of demand.
    if (Math.abs(c.capacity_delta) > Math.abs(c.hours_delta))
      return html`${signedHours(c.capacity_delta, formatHours)}<small>capacity</small>`;
    return html`${signedHours(c.hours_delta, formatHours)}`;
  }

  _list() {
    const all = this._changes;
    if (!all.length)
      return html`<p class="note" data-note="no-changes">
        Nothing moved: every cell of the selection is the same in both runs.
      </p>`;
    const shown = all.slice(0, CHANGES_SHOWN);
    const fmt = this._fmt();
    return html`
      <ol class="changes">
        ${repeat(
          shown,
          (c) => `${c.entity_type}:${c.entity_id}`,
          (c) => html`
            <li data-entity-type=${c.entity_type} data-cause=${c.cause}>
              <span class="delta">${this._deltaOf(c)}</span>
              <span>
                <span class=${`who ${c.withheld ? 'withheld' : ''}`}>${c.label}</span>
                <span class="chip">${causeLabel(c.cause)}</span>
              </span>
              <span class="why">${causeSentence(c, fmt)}</span>
              <span class="meta">
                ${humanize(c.entity_type === 'nonproject' ? 'non-project load' : c.entity_type)} ·
                ${count(c.weeks)} ${c.weeks === 1 ? 'week' : 'weeks'} · ${count(c.rows)}
                ${noun(this._data.dimension, c.rows)}
                ${
                  c.total_hours_delta !== null &&
                  Math.abs(c.total_hours_delta - c.hours_delta) >= 0.01
                    ? html` · ${signedHours(c.total_hours_delta, formatHours)} over every compared
                      week`
                    : nothing
                }
              </span>
            </li>
          `,
        )}
      </ol>
      ${
        all.length > shown.length
          ? html`<p class="note">${count(all.length - shown.length)} more in the table below.</p>`
          : nothing
      }
    `;
  }

  _causes() {
    const k = this._summary;
    if (!k.per_cause.length) return nothing;
    return html`
      <ul class="causes" aria-label="Changed cells by cause">
        ${k.per_cause.map(
          (c) => html`
            <li data-cause=${c.cause}>
              <b>${c.label}</b> ${signedHours(c.hours_delta, formatHours)} · ${count(c.rows)}
              ${c.rows === 1 ? 'cell' : 'cells'}
            </li>
          `,
        )}
      </ul>
    `;
  }

  _shelfList() {
    const shelf = this._shelf;
    if (!shelf.length) return nothing;
    return html`
      <div class="section-title">No capacity to measure against — hours only</div>
      <ul class="shelf" data-shelf>
        ${repeat(
          shelf,
          (e) => e.key,
          (e) => html`
            <li data-entity=${e.key}>
              <span class="delta">${signedHours(e.hours_delta, formatHours)}</span>
              <button
                type="button"
                class="link"
                @click=${() => this._narrowTo(e)}
                title=${`Narrow the list to ${e.label}`}
              >
                ${e.label}
              </button>
              <span class="then">${exact(e.hours_from)} → ${exact(e.hours_to)}</span>
              ${e.cause ? html`<span class="then">· ${causeLabel(e.cause)}</span>` : nothing}
            </li>
          `,
        )}
      </ul>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  get _slopeColumns() {
    // The specification is shared with the server's CSV (lib/changes.js); only the drawing is here.
    return withFormats(slopeColumns({ dimension: this._data?.dimension }), {
      byUnit: {
        hours: (v) => exact(v),
        percent: (v) => percent(v),
        week: (v) => (v ? weekLabel(v) : '—'),
        band: (v) => {
          const state = bandState(v);
          return state ? `${state.icon} ${state.label}` : '—';
        },
        flag: (v) => (v ? 'Yes' : '—'),
      },
      byKey: {
        emphasis: (v) => humanize(v),
        peak_band_changed: (v) => (v ? 'Yes' : '—'),
      },
    });
  }

  get _listColumns() {
    return withFormats(changeListColumns({ dimension: this._data?.dimension }), {
      byUnit: {
        hours: (v) => exact(v),
        percent: (v) => percent(v),
        week: (v) => (v ? weekLabel(v) : '—'),
        band: (v) => {
          const state = bandState(v);
          return state ? `${state.icon} ${state.label}` : '—';
        },
        flag: (v) => (v ? 'Yes' : '—'),
      },
      byKey: { entity_type: (v) => humanize(v) },
    });
  }

  get _cellColumns() {
    return withFormats(changeCellColumns({ dimension: this._data?.dimension }), {
      byUnit: {
        hours: (v) => exact(v),
        percent: (v) => percent(v),
        week: (v) => (v ? weekLabel(v) : '—'),
        band: (v) => {
          const state = bandState(v);
          return state ? `${state.icon} ${state.label}` : '—';
        },
        flag: (v) => (v ? 'Yes' : '—'),
      },
      byKey: { band_changed: (v) => (v ? 'Yes' : '—') },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _subtitle() {
    const d = this._data;
    const { filters } = this.appState;
    if (!d) return '';
    const { substituted } = slopeDimension(filters.dimension);
    const who = d.entity
      ? `${d.entities[0]?.label ?? d.entity} (${d.dimension})`
      : `Every ${noun(d.dimension, 2)} (${d.dimension})`;
    const notes = [];
    if (substituted === 'company') notes.push('the company is one line, so roles are shown');
    if (substituted === 'project')
      notes.push('the diff has no project dimension, so roles are shown');
    return `${who} · run of ${weekLabel(d.planning_date)} against the run of ${weekLabel(
      d.compare.planning_date,
    )} (${compareRoleLabel(d.pair)}) · ${count(d.weeks_count)} weeks from ${weekLabel(d.from)}${
      notes.length ? ` · ${notes.join('; ')}` : ''
    }`;
  }

  _kpis() {
    const d = this._data;
    if (!d) return nothing;
    const k = this._summary;
    return html`
      <div class="kpis">
        <div class="kpi" data-kpi="entities">
          <div class="n">${count(k.entities_changed)} of ${count(k.entities)}</div>
          <div class="l">${noun(d.dimension, 2)} changed</div>
        </div>
        <div class="kpi" data-kpi="crossed">
          <div class="n">${count(k.peaks_crossed)}</div>
          <div class="l">
            ${k.peaks_crossed === 1 ? 'peak' : 'peaks'} crossed a threshold ·
            ${count(k.band_changed)} ${k.band_changed === 1 ? 'cell' : 'cells'} changed band
          </div>
        </div>
        <div class="kpi" data-kpi="hours">
          <div class="n">${signedHours(k.hours_delta, formatHours)}</div>
          <div class="l">
            over the
            window${
              k.capacity_delta !== 0
                ? ` · capacity ${signedHours(k.capacity_delta, formatHours)}`
                : ''
            }
          </div>
        </div>
        <div class="kpi" data-kpi="cause">
          <div class="n">${k.leading_cause ? k.leading_cause.label : '—'}</div>
          <div class="l">
            ${
              k.leading_cause
                ? `moved most: ${signedHours(k.leading_cause.hours_delta, formatHours)} in ${count(
                    k.leading_cause.rows,
                  )} cells`
                : 'no cause: nothing moved'
            }
          </div>
        </div>
      </div>
    `;
  }

  _legend() {
    const pinned = this._lines.some((l) => l.from?.pinned || l.to?.pinned);
    const lineKey = (label, width, opacity, weight) => html`
      <span class="key" data-key=${label.toLowerCase().replace(/\s.*$/, '')}>
        <svg width="26" height="8" aria-hidden="true">
          <line
            x1="0"
            x2="26"
            y1="4"
            y2="4"
            stroke=${weight}
            stroke-width=${width}
            opacity=${opacity}
            stroke-linecap="round"
          />
        </svg>
        <span>${label}</span>
      </span>
    `;
    return html`
      <div class="legend-row">
        ${lineKey('Crossed a threshold', 2.5, 1, 'var(--ink-strong)')}
        ${lineKey('Changed', 1.5, 0.85, 'var(--ink)')}
        ${lineKey('Unchanged', 1, 0.35, 'var(--ink-muted)')}
        ${STATUS_STATES.map(
          (s) => html`
            <span class="key" data-key=${s.key}>
              <svg width="10" height="10" aria-hidden="true">
                <circle cx="5" cy="5" r="4" fill=${`var(${s.var})`} />
              </svg>
              <span class="icon" aria-hidden="true">${s.icon}</span>
              <span>${s.label}</span>
            </span>
          `,
        )}
        ${
          pinned
            ? html`<span class="key" data-key="pinned">
                <span class="icon" aria-hidden="true">▲</span>
                <span
                  >Above ${percent(SLOPE_CAP)}: pinned to the top, the label has the figure</span
                >
              </span>`
            : nothing
        }
      </div>
    `;
  }

  _notes() {
    const d = this._data;
    const k = this._summary;
    const notes = [];
    if (k.thresholds_changed)
      notes.push(
        html`<p class="note" data-note="thresholds">
          The thresholds differ between the runs; each end is banded with the thresholds its run
          stored, so a crossing can come from the thresholds moving rather than the hours.
        </p>`,
      );
    if (d.pair.settings_changed?.length)
      notes.push(
        html`<p class="note" data-note="settings">
          Settings changed between the runs: ${d.pair.settings_changed.join(', ')}.
        </p>`,
      );
    if (d.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${count(d.withheld_count)} changed ${d.withheld_count === 1 ? 'cell' : 'cells'} about
          people outside your scope ${d.withheld_count === 1 ? 'is' : 'are'} withheld.
        </p>`,
      );
    if (k.folded.count > 0)
      notes.push(
        html`<p class="note" data-note="folded">
          ${count(k.folded.count)} smaller contributors are folded into the totals
          (${signedHours(k.folded.hours_delta, formatHours)}); the list names the rest.
        </p>`,
      );
    return notes;
  }

  render() {
    const { run } = this.appState;
    const d = this._data;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to compare.</div>`;
    else if (this._noComparison)
      body = html`<div class="state" data-state="no-comparison">
        Only one forecast run has succeeded, so there is nothing to compare it with yet. The next
        run will have this one to look back on.
      </div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Comparing the runs…</div>`;
    else if (!d || d.weeks_count === 0)
      body = html`<div class="state">
        The two runs share no weeks in this window — nothing to compare.
      </div>`;
    else
      body = html`
        <chart-frame
          .categories=${[]}
          .max=${this._axisMax}
          .marks=${this._marks}
          .formatValue=${(v) => percent(v)}
          value-label="Peak allocation"
          height="380"
        ></chart-frame>
        ${this._legend()} ${this._shelfList()} ${this._notes()}
        <div class="section-title">
          ${
            this._narrowed
              ? html`What moved ${d.entities[0]?.label ?? d.entity}, largest first ·
                  <button type="button" class="link" @click=${() => setFilters({ entity: null })}>
                    every ${noun(d.dimension, 2)}
                  </button>`
              : 'What moved, largest first'
          }
        </div>
        ${this._causes()} ${this._list()}
        <details
          class="table"
          ?open=${this._slopeOpen}
          @toggle=${(e) => (this._slopeOpen = e.target.open)}
        >
          <summary>Table view: peaks per ${noun(d.dimension, 1)}</summary>
          <data-table
            caption=${`Each ${noun(d.dimension, 1)}'s peak allocation in both runs — ${this._subtitle()}`}
            export-name="changes-slope"
            page-size="25"
            dense
            .columns=${this._slopeColumns}
            .rows=${slopeExportRows(d.entities)}
          ></data-table>
        </details>
        <details
          class="table"
          ?open=${this._listOpen}
          @toggle=${(e) => (this._listOpen = e.target.open)}
        >
          <summary>Table view: changes</summary>
          <data-table
            caption=${`Every attributed change, largest movement first — ${this._subtitle()}`}
            export-name="changes-list"
            page-size="25"
            dense
            .columns=${this._listColumns}
            .rows=${changeExportRows(d.changes, this._fmt())}
          ></data-table>
        </details>
        <details
          class="table"
          ?open=${this._cellsOpen}
          @toggle=${(e) => (this._cellsOpen = e.target.open)}
        >
          <summary>Table view: changed cells</summary>
          <data-table
            caption=${`Every heatmap cell that differs between the runs — ${this._subtitle()}`}
            export-name="changes-cells"
            page-size="25"
            dense
            .columns=${this._cellColumns}
            .rows=${cellExportRows(d.rows)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Changes</div>
            <h2>What moved since the last forecast, and why?</h2>
            <div class="sub">${this._subtitle()}</div>
          </div>
          ${this._kpis()}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('snapshot-changes', SnapshotChanges);
